import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export const EmailImportModal = ({ open, onOpenChange, onImport }) => {
  const [emailContent, setEmailContent] = useState('');
  const [subject, setSubject] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!emailContent.trim()) return;

    setLoading(true);
    setResult(null);

    try {
      const res = await onImport(emailContent, subject, fromEmail);
      setResult({
        success: true,
        message: `${res.tickets_created} billet(s) créé(s), ${res.matches_found} match(s) trouvé(s)`,
        tickets: res.tickets,
      });
      
      // Reset form after success
      setTimeout(() => {
        setEmailContent('');
        setSubject('');
        setFromEmail('');
        setResult(null);
        onOpenChange(false);
      }, 2000);
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors de l\'import',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmailContent('');
      setSubject('');
      setFromEmail('');
      setResult(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Mail className="h-5 w-5 text-primary" />
            Importer un Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm text-muted-foreground">
              Sujet de l'email (optionnel)
            </Label>
            <Input
              id="subject"
              data-testid="email-subject-input"
              placeholder="Ex: Confirmation de commande - Ticketmaster"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-background border-border/50"
            />
          </div>

          {/* From Email */}
          <div className="space-y-2">
            <Label htmlFor="from" className="text-sm text-muted-foreground">
              Email expéditeur (optionnel)
            </Label>
            <Input
              id="from"
              data-testid="email-from-input"
              placeholder="Ex: noreply@ticketmaster.fr"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              className="bg-background border-border/50"
            />
          </div>

          {/* Email Content */}
          <div className="space-y-2">
            <Label htmlFor="content" className="text-sm text-muted-foreground">
              Contenu de l'email *
            </Label>
            <Textarea
              id="content"
              data-testid="email-content-textarea"
              placeholder="Collez ici le contenu complet de l'email (texte ou HTML)..."
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              className="min-h-[300px] bg-background border-border/50 font-mono text-sm"
            />
          </div>

          {/* Result */}
          {result && (
            <div
              data-testid="import-result"
              className={`flex items-center gap-2 p-3 rounded-md ${
                result.success
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'bg-red-500/10 text-red-400 border border-red-500/20'
              }`}
            >
              {result.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <span className="text-sm">{result.message}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="border-border/50"
            >
              Annuler
            </Button>
            <Button
              data-testid="parse-email-button"
              onClick={handleImport}
              disabled={loading || !emailContent.trim()}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Analyser et Importer
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailImportModal;
